import React from "react";
import { Routes, Route } from "react-router-dom";
import LeadGenerator from "./pages/LeadGenerator";
import LeadGeneratorV2 from "./pages/LeadGeneratorV2";
import Login from "./pages/Login";
import SignupWithTrial from "./pages/SignupWithTrial";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminPanel from "./pages/AdminPanel";
import AdminPanelSimple from "./pages/AdminPanelSimple";
import EnhancedAdminPanel from "./pages/EnhancedAdminPanel";
import CampaignForm from "./pages/CampaignForm";
import EditCampaignPage from "./pages/EditCampaignPage";
import CreateCampaignForm from "./components/CreateCampaignForm";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import UserRoleGate from "./components/UserRoleGate";
import ExportSuccess from "./pages/ExportSuccess";
import ExportCancel from "./pages/ExportCancel";
import ProfileSettings from "./pages/ProfileSettings";
import CampaignPerformance from "./pages/CampaignPerformance";
// Epoch 26: Using regular Dashboard as AuthenticatedRoute provides the layout
import Dashboard from "./pages/Dashboard";
import CampaignsList from "./pages/CampaignsList";
import LeadExportHistory from "./pages/LeadExportHistory";
import Version from "./pages/Version";
import CreateCampaignWizard from "./pages/CreateCampaignWizard";
import { AnalyticsDashboard } from "./components/Analytics/AnalyticsDashboard";
import CompleteProfile from "./pages/CompleteProfile";
import AuthenticatedRoute from "./components/auth/AuthenticatedRoute";
import VerifyCompanyJoin from "./pages/VerifyCompanyJoin";
import LeadsInbox from "./components/lead-responses/LeadsInbox";
import CampaignApprovals from "./pages/CSR/CampaignApprovals";
import CSRLeadResponseFormPage from "./pages/CSRLeadResponseFormPage"; // EPOCH 29
import CSRCompanies from "./pages/CSR/CSRCompanies"; // EPOCH 35
import CSRUsers from "./pages/CSR/CSRUsers"; // EPOCH 35
import CSRCampaigns from "./pages/CSR/CSRCampaigns"; // EPOCH 35 - CSR Campaigns Dashboard
import CSRCampaignDetails from "./pages/CSR/CSRCampaignDetails"; // EPOCH 42 - CSR Campaign Details View
import CSRFilterRequests from "./pages/CSR/CSRFilterRequests"; // EPOCH 36 - Filter Request Management
import CSRCampaignTest from "./pages/CSR/CSRCampaignTest"; // EPOCH 35 Phase 2 Testing
import CSRCampaignCreate from "./pages/CSR/CSRCampaignCreate"; // EPOCH 35 Phase 3
import CSRRecordUsage from "./pages/CSR/CSRRecordUsage"; // EPOCH 53 - Record Outreach Usage
import CampaignGroupConfig from "./components/reports/CampaignGroupConfig"; // EPOCH 80 - CSR group setup
import CSRReports from "./pages/CSR/CSRReports"; // EPOCH 80 - Weekly report review
import CSRReportDetail from "./pages/CSR/CSRReportDetail"; // EPOCH 80 - Report detail/edit
import CSRICPSignalList from "./pages/CSR/CSRICPSignalList"; // EPOCH 83 - ICP Signal Classifier
import CSRICPSignalUpload from "./pages/CSR/CSRICPSignalUpload"; // EPOCH 83 - ICP Signal Upload
import CSRICPReview from "./pages/CSR/CSRICPReview"; // EPOCH 83 - ICP Signal Layer 2 Review
import CSRICPVerdict from "./pages/CSR/CSRICPVerdict"; // EPOCH 83 - ICP Signal Verdict
import OutreachTeamManagement from "./components/admin/OutreachTeamManagement";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import AuthCallback from "./pages/AuthCallback";
import TopQualifierHostedChat from "./pages/TopQualifierHostedChat"; // EPOCH 52 Phase 3
import Credits from "./pages/Credits"; // EPOCH 53 - Outreach Credits Management
import Reports from "./pages/Reports"; // EPOCH 80 - Client intelligence reports
import ReportDetail from "./pages/ReportDetail"; // EPOCH 80 - Report detail view

const AppRoutes: React.FC = () => {
  return (
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/landing" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignupWithTrial />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/complete-profile" element={<CompleteProfile />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* EPOCH 55: Pricing page is public for marketing purposes */}
        <Route path="/pricing" element={<Pricing />} />

        {/* EPOCH 52 Phase 3: Top Qualifier Hosted Chat - Public access for leads */}
        <Route path="/top-qualifier/:slug" element={<TopQualifierHostedChat />} />
        
        {/* Protected routes with authentication and trial banner */}
        <Route path="/*" element={
          <AuthenticatedRoute>
            <Routes>
              {/* Encore Lead Generator Route - Epoch 19 Enhanced Version */}
              <Route path="/lead-generator" element={<LeadGeneratorV2 />} />
              <Route path="/lead-generator-legacy" element={<LeadGenerator />} />
              <Route path="/export-history" element={<LeadExportHistory />} />
              <Route path="/export-success" element={<ExportSuccess />} />
              <Route path="/export-cancel" element={<ExportCancel />} />
              <Route path="/profile" element={<ProfileSettings />} />
              
              {/* Regular user routes */}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/app" element={<Dashboard />} />
              <Route path="/version" element={<Version />} />

              {/* EPOCH 53: Outreach Credits Management */}
              <Route path="/credits" element={<Credits />} />

              {/* EPOCH 80: Client Intelligence Reports */}
              <Route path="/reports" element={<Reports />} />
              <Route path="/reports/:id" element={<ReportDetail />} />
              <Route path="/campaigns" element={<CampaignsList />} />
              <Route path="/campaign-performance" element={<CampaignPerformance />} />
              <Route path="/analytics" element={<AnalyticsDashboard />} />
              <Route path="/leads-inbox" element={<LeadsInbox />} />
              {/* EPOCH 29: Unified form for new responses and follow-ups */}
              <Route path="/leads-inbox/add" element={<CSRLeadResponseFormPage />} />
              <Route path="/verify-company/:code" element={<VerifyCompanyJoin />} />
              <Route path="/create-campaign" element={<CreateCampaignWizard />} />
              <Route path="/create-campaign-wizard" element={<CreateCampaignWizard />} />
              <Route path="/create-campaign-legacy" element={<CreateCampaignForm />} />
              <Route path="/campaign/:id/form" element={<CampaignForm />} />
              <Route path="/campaign/:id/edit" element={<EditCampaignPage />} />
              
              {/* Legacy CSR Dashboard - redirect to CSR campaigns */}
              <Route 
                path="/csr-dashboard" 
                element={<CSRCampaigns />}
              />
              
              {/* CSR Campaigns Dashboard - Shows all campaigns assigned to CSR */}
              <Route 
                path="/csr-campaigns" 
                element={<CSRCampaigns />}
              />
              
              {/* CSR Campaign Details - View individual campaign details */}
              <Route 
                path="/campaigns/:id" 
                element={<CSRCampaignDetails />}
              />
              
              {/* Epoch 35: CSR Management Pages */}
              <Route path="/csr-companies" element={<CSRCompanies />} />
              <Route path="/csr-users" element={<CSRUsers />} />
              
              {/* Epoch 36: CSR Filter Request Management */}
              <Route path="/csr-filter-requests" element={<CSRFilterRequests />} />

              {/* Epoch 53: CSR Record Outreach Usage - CSR only */}
              <Route
                path="/csr-record-usage"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CSRRecordUsage />
                  </RoleProtectedRoute>
                }
              />

              {/* Epoch 80: CSR Campaign Group Setup - CSR only */}
              <Route
                path="/csr-group-config"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CampaignGroupConfig />
                  </RoleProtectedRoute>
                }
              />

              {/* Epoch 80: CSR Weekly Report Review - CSR only */}
              <Route
                path="/csr-reports"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CSRReports />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/csr-reports/:id"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CSRReportDetail />
                  </RoleProtectedRoute>
                }
              />

              {/* Epoch 83: ICP Signal Classifier - CSR only */}
              <Route
                path="/csr-icp-signal"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CSRICPSignalList />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/csr-icp-signal/new"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CSRICPSignalUpload />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/csr-icp-signal/:jobId/review"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CSRICPReview />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/csr-icp-signal/:jobId/verdict"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CSRICPVerdict />
                  </RoleProtectedRoute>
                }
              />

              {/* Epoch 35 Phase 2: CSR Campaign Test Page - CSR only */}
              <Route
                path="/csr-campaign-test"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CSRCampaignTest />
                  </RoleProtectedRoute>
                }
              />
              
              {/* CSR Campaign Creation - EPOCH 35 Phase 3 */}
              <Route
                path="/csr-campaign-create"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CSRCampaignCreate />
                  </RoleProtectedRoute>
                }
              />
              
              {/* Admin Panel - Enhanced version with full user management (Epoch 6) */}
              <Route
                path="/admin"
                element={
                  <RoleProtectedRoute requiredRole="admin">
                    <EnhancedAdminPanel />
                  </RoleProtectedRoute>
                }
              />
              
              {/* Legacy Simple Admin Panel - kept for backward compatibility */}
              <Route
                path="/admin-simple"
                element={
                  <RoleProtectedRoute requiredRole="admin">
                    <AdminPanelSimple />
                  </RoleProtectedRoute>
                }
              />
              
              {/* Full Admin Panel - requires admin API access */}
              <Route
                path="/admin-full"
                element={
                  <RoleProtectedRoute requiredRole="admin">
                    <AdminPanel />
                  </RoleProtectedRoute>
                }
              />
              
              {/* CSR Campaign Approvals - for CSRs to approve submitted campaigns */}
              <Route
                path="/campaign-approvals"
                element={
                  <RoleProtectedRoute requiredRole="csr">
                    <CampaignApprovals />
                  </RoleProtectedRoute>
                }
              />
              
              {/* Epoch 21: Outreach Team Management - Admin only */}
              <Route
                path="/admin/outreach-team"
                element={
                  <RoleProtectedRoute requiredRole="admin">
                    <OutreachTeamManagement />
                  </RoleProtectedRoute>
                }
              />

              {/* Legacy admin route using UserRoleGate for backward compatibility */}
              <Route
                path="/admin-legacy"
                element={
                  <UserRoleGate allowedRoles={["admin"]}>
                    <AdminPanelSimple />
                  </UserRoleGate>
                }
              />
            </Routes>
          </AuthenticatedRoute>
        } />
      </Routes>
  );
};

export default AppRoutes;


